// Pure bookmark-state logic, kept out of the hook so it can be unit-tested.

// Server truth overlaid with this session's in-flight toggles, so tapping a
// bookmark feels instant while the mutation is still going.
export function mergeBookmarks(
  server: string[] | undefined,
  optimistic: Record<string, boolean>,
): Set<string> {
  const merged = new Set(server || []);
  Object.entries(optimistic).forEach(([slug, bookmarked]) => {
    if (bookmarked) merged.add(slug);
    else merged.delete(slug);
  });
  return merged;
}

// Confirmation copy for a toggle. Lives here so the grid card, the detail page
// and the scroll-away bar can never word it differently — QA 2.2 pins the exact
// strings.
export function bookmarkToast(bookmarked: boolean): string {
  return bookmarked ? 'Added to bookmarks' : 'Removed from bookmarks';
}

// Every list is alphabetical by title, with bookmarked lessons pinned above the
// rest. Ticket AC 7 originally kept the catalog's own order for non-bookmarked
// lessons; Bailey's QA pass (2026-08-11) asked for alphabetical throughout, so
// both groups now sort — this is a deliberate divergence from the prototype.
export function sortBookmarkedFirst<T>(
  items: T[],
  slugOf: (item: T) => string,
  bookmarks: Set<string>,
  titleOf: (item: T) => string,
): T[] {
  const byTitle = (a: T, b: T) => titleOf(a)
    .localeCompare(titleOf(b), undefined, { sensitivity: 'base' });
  const bookmarked = items.filter((item) => bookmarks.has(slugOf(item))).sort(byTitle);
  const rest = items.filter((item) => !bookmarks.has(slugOf(item))).sort(byTitle);
  return [...bookmarked, ...rest];
}
